import { Icon } from '@iconify/react';
import { Button, Spinner, Tooltip } from '@nextui-org/react';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import DataView from '../../components/data-view/data-view';
import ListCard from '../../components/list-card';
import { TableConfig } from '../../components/table/types';
import { useCreateOrUpdatePractice } from '../../hooks/queries/practices';
import PromptService from '../../services/prompt-service';
import { useActiveTenant } from '@/hooks/useActiveTenant';
import { PromptData, StandardResponse } from '../../types/agent';

const UserProfiles = () => {
  const navigate = useNavigate();
  const { tenantId } = useActiveTenant();
  const [loadingProfileId, setLoadingProfileId] = React.useState<number | null>(
    null
  );
  const [viewType, setViewType] = React.useState<'tile' | 'list'>('tile');
  const createPracticeMutation = useCreateOrUpdatePractice();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();

  const { data, isLoading, isError } = useQuery<StandardResponse<PromptData>>({
    queryKey: ['agents', { is_active: true, tag: 'UserProfile' }],
    queryFn: async (): Promise<StandardResponse<PromptData>> => {
      const response = await PromptService.getPrompts({
        filters: {
          page: 1,
          is_active: true,
          tag: 'UserProfile',
        },
      });
      return response as StandardResponse<PromptData>;
    },
  });

  const handleProfileClick = async (profile: PromptData) => {
    try {
      setLoadingProfileId(profile.id);
      const response = (await createPracticeMutation.mutateAsync({
        name: profile.name,
        org_id: tenantId,
      })) as { id: number };

      navigate(
        `/${tenantSlug}/user-profiles/${response.id}/run?action=${encodeURIComponent(profile.name)}&id=${response.id}`
      );
    } catch (error) {
      console.log(error);
      toast.error('Failed to load profile');
    } finally {
      setLoadingProfileId(null);
    }
  };

  if (isLoading)
    return (
      <div className="flex h-full min-h-0 flex-1 items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  if (isError)
    return (
      <div className="text-center text-danger">
        Error loading profiles. Please try again later.
      </div>
    );

  const profileTableConfig: TableConfig = {
    columns: [
      {
        uid: 'name',
        name: 'Name',
        sortable: true,
      },
      {
        uid: 'actions',
        name: 'Actions',
        renderCell: (profile: PromptData) => (
          <div className="flex gap-2">
            <Tooltip content="View">
              <Button
                isIconOnly
                variant="light"
                size="sm"
                onClick={() => handleProfileClick(profile)}
                isLoading={loadingProfileId === profile.id}
                aria-label="View"
              >
                <Icon icon="solar:eye-bold" width="20" />
              </Button>
            </Tooltip>
          </div>
        ),
      },
    ],
    initialVisibleColumns: [
      'name',
      'role',
      'organization',
      'description',
      'actions',
    ],
    searchable: true,
    searchPlaceholder: 'Search profiles...',
    searchKey: 'name',
    pagination: {
      enabled: true,
      initialRowsPerPage: 100,
      rowsPerPageOptions: [],
      showControls: true,
      showShadow: true,
    },
    title: 'User Profiles',
    stickyHeader: true,
    maxHeight: '700px',
    showSelectedItems: false,
  };

  // const actions = (
  //   <Button
  //     color="primary"
  //     isDisabled
  //     onClick={() => navigate("/user-profile/create")}
  //     startContent={<Icon icon="solar:add-circle-bold" width="20" />}
  //   >
  //     Create Profile
  //   </Button>
  // );

  const renderCard = (profile: PromptData) => (
    <ListCard
      key={profile.id}
      title={profile.name}
      onStart={() => handleProfileClick(profile)}
      isStarting={loadingProfileId === profile.id}
    />
  );

  return (
    <DataView<PromptData>
      title="User Profiles"
      data={data?.results || []}
      tableConfig={profileTableConfig}
      viewType={viewType}
      onViewChange={setViewType}
      renderCard={renderCard}
    />
  );
};

export default UserProfiles;
